// TennisTrack Hub — Pannello Admin | 029 Project
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import './Admin.css';

// ──────────────────────────────────────────────
// Sezione: Dashboard statistiche
// ──────────────────────────────────────────────
const SezioneStats = ({ stats, loading }) => {
  if (loading) return <div className="loading-msg">Caricamento statistiche...</div>;
  if (!stats) return null;
  return (
    <div>
      <div className="admin-stats-grid">
        <div className="admin-stat">
          <span className="admin-stat-num">{stats.utenti?.totale || 0}</span>
          <span className="admin-stat-label">Utenti totali</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat-num" style={{ color: 'var(--color-primary)' }}>
            {stats.utenti?.premium || 0}
          </span>
          <span className="admin-stat-label">Utenti Pro</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat-num" style={{ color: 'var(--color-accent)' }}>
            {stats.atleti || 0}
          </span>
          <span className="admin-stat-label">Atleti attivi</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat-num" style={{ color: '#ffb347' }}>
            {stats.template?.in_revisione || 0}
          </span>
          <span className="admin-stat-label">In revisione</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat-num">{stats.template?.approvato || 0}</span>
          <span className="admin-stat-label">Template approvati</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat-num">{stats.abbonamenti_attivi || 0}</span>
          <span className="admin-stat-label">Abbonamenti attivi</span>
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────
// Sezione: Revisione template
// ──────────────────────────────────────────────
const SezioneRevisioni = () => {
  const [template, setTemplate] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalRifiuto, setModalRifiuto] = useState(null); // { id, titolo }
  const [notaRifiuto, setNotaRifiuto] = useState('');
  const [preview, setPreview] = useState(null); // { id, titolo, contenuto }
  const [actionLoading, setActionLoading] = useState(null);
  const [errore, setErrore] = useState('');

  const carica = useCallback(async () => {
    try {
      const res = await api.get('/admin/template/revisione');
      setTemplate(res.data.template);
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore nel caricamento.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const approva = async (id, piano = 'free') => {
    setActionLoading(id + '_approva');
    setErrore('');
    try {
      await api.patch(`/admin/template/${id}/approva`, { piano });
      await carica();
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore.');
    } finally {
      setActionLoading(null);
    }
  };

  const apriRifiuto = (t) => {
    setModalRifiuto(t);
    setNotaRifiuto('');
  };

  const confermaRifiuto = async () => {
    if (!notaRifiuto.trim()) return;
    setActionLoading(modalRifiuto.id + '_rifiuta');
    setErrore('');
    try {
      await api.patch(`/admin/template/${modalRifiuto.id}/rifiuta`, { nota: notaRifiuto });
      setModalRifiuto(null);
      await carica();
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore.');
    } finally {
      setActionLoading(null);
    }
  };

  const formatData = (iso) => iso
    ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  if (loading) return <div className="loading-msg">Caricamento...</div>;

  return (
    <div>
      {errore && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{errore}</div>}

      {template.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
          ✅ Nessun template in attesa di revisione.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="revisioni-table">
            <thead>
              <tr>
                <th>Template</th>
                <th>Tipo</th>
                <th>Autore</th>
                <th>Data invio</th>
                <th>Piano proposto</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {template.map(t => {
                const contenuto = typeof t.contenuto === 'string'
                  ? JSON.parse(t.contenuto || '{}')
                  : (t.contenuto || {});
                const nEsercizi = contenuto?.esercizi?.length || 0;

                return (
                  <tr key={t.id}>
                    <td>
                      <strong
                        style={{ color: 'var(--color-primary)', cursor: 'pointer' }}
                        onClick={() => setPreview(preview?.id === t.id ? null : { ...t, contenuto })}
                      >
                        {t.titolo}
                      </strong>
                      {t.descrizione && (
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                          {t.descrizione.substring(0, 80)}{t.descrizione.length > 80 ? '…' : ''}
                        </div>
                      )}
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                        {nEsercizi} esercizi · {t.livello}
                      </div>
                      {preview?.id === t.id && (
                        <div className="template-preview-panel">
                          {JSON.stringify(preview.contenuto, null, 2)}
                        </div>
                      )}
                    </td>
                    <td>
                      {t.tipo === 'tennis' ? '🎾' : '💪'} {t.tipo}
                    </td>
                    <td>
                      <div>{t.autore_nome} {t.autore_cognome}</div>
                      <div className="rev-autore">{t.autore_email}</div>
                      <div className="rev-autore">piano: {t.autore_piano}</div>
                    </td>
                    <td>{formatData(t.created_at)}</td>
                    <td>
                      <select
                        className="piano-select"
                        defaultValue="free"
                        id={`piano-${t.id}`}
                      >
                        <option value="demo">Demo</option>
                        <option value="free">Free</option>
                        <option value="pro">Pro</option>
                      </select>
                    </td>
                    <td>
                      <div className="rev-actions">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            const sel = document.getElementById(`piano-${t.id}`);
                            approva(t.id, sel?.value || 'free');
                          }}
                          disabled={actionLoading === t.id + '_approva'}
                        >
                          {actionLoading === t.id + '_approva' ? '...' : '✅ Approva'}
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => apriRifiuto(t)}
                          disabled={actionLoading === t.id + '_rifiuta'}
                        >
                          ❌ Rifiuta
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal rifiuto */}
      {modalRifiuto && (
        <div className="modal-overlay" onClick={() => setModalRifiuto(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>❌ Rifiuta template</h3>
            <p style={{ marginBottom: '12px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Stai rifiutando: <strong style={{ color: 'var(--text-primary)' }}>{modalRifiuto.titolo}</strong>
            </p>
            <textarea
              placeholder="Motiva il rifiuto (obbligatorio) — sarà visibile all'autore nella notifica..."
              value={notaRifiuto}
              onChange={e => setNotaRifiuto(e.target.value)}
              maxLength={400}
            />
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setModalRifiuto(null)}>Annulla</button>
              <button
                className="btn btn-danger"
                onClick={confermaRifiuto}
                disabled={!notaRifiuto.trim() || actionLoading}
              >
                {actionLoading ? '...' : '❌ Conferma rifiuto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────
// Sezione: Tutti i template
// ──────────────────────────────────────────────
const SezioneTuttiTemplate = () => {
  const [template, setTemplate] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStato, setFiltroStato] = useState('');
  const [filtroCerca, setFiltroCerca] = useState('');

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroStato) params.set('stato', filtroStato);
      if (filtroCerca) params.set('cerca', filtroCerca);
      const res = await api.get(`/admin/template?${params.toString()}`);
      setTemplate(res.data.template);
    } catch {
      /* silenzioso */
    } finally {
      setLoading(false);
    }
  }, [filtroStato, filtroCerca]);

  useEffect(() => { carica(); }, [carica]);

  const cambiaPiano = async (id, piano) => {
    try {
      await api.patch(`/admin/template/${id}/piano`, { piano });
      await carica();
    } catch {
      alert('Errore nel cambio piano.');
    }
  };

  const eliminaTemplate = async (id, titolo) => {
    if (!window.confirm(`Eliminare "${titolo}" dalla libreria? Azione irreversibile.`)) return;
    try {
      await api.delete(`/admin/template/${id}`);
      await carica();
    } catch {
      alert('Errore eliminazione.');
    }
  };

  const formatData = (iso) => iso
    ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <input
          type="text"
          className="form-input"
          placeholder="Cerca titolo..."
          value={filtroCerca}
          onChange={e => setFiltroCerca(e.target.value)}
          style={{ maxWidth: '220px' }}
        />
        <select className="form-select" value={filtroStato} onChange={e => setFiltroStato(e.target.value)} style={{ maxWidth: '160px' }}>
          <option value="">Tutti gli stati</option>
          <option value="bozza">Bozza</option>
          <option value="in_revisione">In revisione</option>
          <option value="approvato">Approvato</option>
          <option value="rifiutato">Rifiutato</option>
        </select>
      </div>

      {loading ? <div className="loading-msg">Caricamento...</div> : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="revisioni-table">
            <thead>
              <tr>
                <th>Titolo</th>
                <th>Tipo</th>
                <th>Autore</th>
                <th>Stato</th>
                <th>Piano</th>
                <th>Utilizzi</th>
                <th>Data</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {template.map(t => (
                <tr key={t.id}>
                  <td style={{ maxWidth: '180px' }}>
                    <strong style={{ fontSize: '0.88rem' }}>{t.titolo}</strong>
                  </td>
                  <td>{t.tipo === 'tennis' ? '🎾' : '💪'}</td>
                  <td>
                    <div style={{ fontSize: '0.82rem' }}>{t.autore_nome} {t.autore_cognome}</div>
                    <div className="rev-autore">{t.autore_email || '—'}</div>
                  </td>
                  <td>
                    <span className={`stato-badge stato-${t.stato}`}>{t.stato}</span>
                  </td>
                  <td>
                    <select
                      className="piano-select"
                      value={t.piano}
                      onChange={e => cambiaPiano(t.id, e.target.value)}
                    >
                      <option value="demo">Demo</option>
                      <option value="free">Free</option>
                      <option value="pro">Pro</option>
                    </select>
                  </td>
                  <td style={{ textAlign: 'center' }}>{t.n_utilizzi}</td>
                  <td>{formatData(t.created_at)}</td>
                  <td>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => eliminaTemplate(t.id, t.titolo)}
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
              {template.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>Nessun template trovato.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────
// Sezione: Utenti
// ──────────────────────────────────────────────
const SezioneUtenti = () => {
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/utenti')
      .then(res => setUtenti(res.data.utenti))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const formatData = (iso) => iso
    ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  if (loading) return <div className="loading-msg">Caricamento...</div>;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <table className="utenti-table">
        <thead>
          <tr>
            <th>Nome</th>
            <th>Email</th>
            <th>Piano</th>
            <th>Atleti</th>
            <th>Template</th>
            <th>Registrato</th>
          </tr>
        </thead>
        <tbody>
          {utenti.map(u => (
            <tr key={u.id}>
              <td>{u.nome} {u.cognome}</td>
              <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{u.email}</td>
              <td>
                <span className={`t-badge t-badge-${u.piano}`} style={{ textTransform: 'uppercase' }}>
                  {u.piano === 'pro' ? '⭐ ' : ''}{u.piano}
                </span>
              </td>
              <td style={{ textAlign: 'center' }}>{u.n_atleti}</td>
              <td style={{ textAlign: 'center' }}>{u.n_template}</td>
              <td>{formatData(u.created_at)}</td>
            </tr>
          ))}
          {utenti.length === 0 && (
            <tr><td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>Nessun utente.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ──────────────────────────────────────────────
// Sezione: Gestione utenti avanzata (reset pw, sospensione, log)
// ──────────────────────────────────────────────
const SezioneUtentiAvanzata = () => {
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [utenteSelezionato, setUtenteSelezionato] = useState(null);
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [azione, setAzione] = useState(null); // { tipo: 'reset'|'sospendi'|'riattiva'|'ruolo', utente, valore }
  const [motivoSospensione, setMotivoSospensione] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [esito, setEsito] = useState('');
  const [errore, setErrore] = useState('');

  const RUOLI_LABEL = {
    player:    '🎾 Player',
    coach:     '🎓 Coach',
    club:      '🏟 Club',
    moderator: '🛡 Moderatore',
    admin:     '⚡ Admin',
  };

  const cambiaRuolo = async (utente, nuovoRuolo) => {
    try {
      await api.patch(`/admin/utenti/${utente.id}/ruolo`, { ruolo_app: nuovoRuolo });
      setUtenti(prev => prev.map(u => u.id === utente.id ? { ...u, ruolo_app: nuovoRuolo } : u));
      setEsito(`✅ Ruolo di ${utente.nome} ${utente.cognome} aggiornato a "${nuovoRuolo}".`);
      setTimeout(() => setEsito(''), 4000);
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore cambio ruolo.');
      setTimeout(() => setErrore(''), 4000);
    }
  };

  const cambiaPiano = async (utente, nuovoPiano) => {
    try {
      await api.patch(`/admin/utenti/${utente.id}/piano`, { piano: nuovoPiano });
      setUtenti(prev => prev.map(u => u.id === utente.id ? { ...u, piano: nuovoPiano } : u));
      setEsito(`✅ Piano di ${utente.nome} ${utente.cognome} aggiornato a "${nuovoPiano}".`);
      setTimeout(() => setEsito(''), 4000);
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore cambio piano.');
      setTimeout(() => setErrore(''), 4000);
    }
  };

  const carica = useCallback(async () => {
    try {
      const res = await api.get('/admin/utenti');
      setUtenti(res.data.utenti);
    } catch {/* silenzioso */}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const caricaLog = async (id) => {
    setLogLoading(true);
    setLog([]);
    try {
      const res = await api.get(`/admin/utenti/${id}/log-accessi`);
      setLog(res.data.log);
    } catch {/* silenzioso */}
    finally { setLogLoading(false); }
  };

  const selezionaUtente = (u) => {
    if (utenteSelezionato?.id === u.id) {
      setUtenteSelezionato(null);
      setLog([]);
    } else {
      setUtenteSelezionato(u);
      caricaLog(u.id);
    }
  };

  const eseguiAzione = async () => {
    if (!azione) return;
    setActionLoading(true);
    setEsito('');
    setErrore('');
    try {
      if (azione.tipo === 'reset') {
        const res = await api.post(`/admin/utenti/${azione.utente.id}/reset-password`);
        setEsito(`✅ Link reset inviato${res.data.email_inviata ? ' via email' : ' (email non configurata — link: ' + res.data.reset_link + ')'}.`);
      } else if (azione.tipo === 'sospendi') {
        if (!motivoSospensione.trim()) {
          setErrore('Il motivo della sospensione è obbligatorio.');
          setActionLoading(false);
          return;
        }
        await api.patch(`/admin/utenti/${azione.utente.id}/sospendi`, { sospendi: true, motivo: motivoSospensione });
        setEsito(`✅ Account di ${azione.utente.nome} ${azione.utente.cognome} sospeso.`);
        await carica();
      } else if (azione.tipo === 'riattiva') {
        await api.patch(`/admin/utenti/${azione.utente.id}/sospendi`, { sospendi: false });
        setEsito(`✅ Account di ${azione.utente.nome} ${azione.utente.cognome} riattivato.`);
        await carica();
      }
      setAzione(null);
      setMotivoSospensione('');
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatData = (iso) => iso
    ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

  if (loading) return <div className="loading-msg">Caricamento...</div>;

  return (
    <div>
      {esito && <div className="alert alert-success" style={{ marginBottom: '16px' }}>{esito}</div>}
      {errore && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{errore}</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="utenti-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Piano</th>
              <th>Ruolo</th>
              <th>Stato</th>
              <th>Registrato</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {utenti.map(u => (
              <React.Fragment key={u.id}>
                <tr
                  style={{ cursor: 'pointer', opacity: u.sospeso ? 0.6 : 1 }}
                  onClick={() => selezionaUtente(u)}
                >
                  <td>
                    <strong style={{ color: utenteSelezionato?.id === u.id ? 'var(--color-primary)' : 'var(--text-primary)' }}>
                      {u.nome} {u.cognome}
                    </strong>
                  </td>
                  <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{u.email}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <select
                      value={u.piano || 'free'}
                      onChange={e => cambiaPiano(u, e.target.value)}
                      style={{
                        fontSize: '0.78rem',
                        padding: '3px 6px',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--bg-card)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="free">Free</option>
                      <option value="pro">⭐ Pro</option>
                      <option value="demo">Demo</option>
                    </select>
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <select
                      value={u.ruolo_app || 'coach'}
                      onChange={e => cambiaRuolo(u, e.target.value)}
                      style={{
                        fontSize: '0.78rem',
                        padding: '3px 6px',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--bg-card)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer'
                      }}
                    >
                      {Object.entries(RUOLI_LABEL).map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {u.sospeso
                      ? <span style={{ fontSize: '0.78rem', color: 'var(--color-error)', fontWeight: 600 }}>🔴 Sospeso</span>
                      : <span style={{ fontSize: '0.78rem', color: '#4caf50' }}>🟢 Attivo</span>
                    }
                  </td>
                  <td style={{ fontSize: '0.82rem' }}>{new Date(u.created_at).toLocaleDateString('it-IT')}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="rev-actions">
                      <button
                        className="btn btn-secondary btn-sm"
                        title="Invia reset password"
                        onClick={() => setAzione({ tipo: 'reset', utente: u })}
                      >
                        🔑 Reset pw
                      </button>
                      {u.sospeso ? (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => setAzione({ tipo: 'riattiva', utente: u })}
                        >
                          ✅ Riattiva
                        </button>
                      ) : (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => setAzione({ tipo: 'sospendi', utente: u })}
                        >
                          🔒 Sospendi
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {/* Log accessi espanso */}
                {utenteSelezionato?.id === u.id && (
                  <tr>
                    <td colSpan={7} style={{ background: 'rgba(0,0,0,0.2)', padding: '16px 20px' }}>
                      <strong style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        📋 Ultimi accessi di {u.nome} {u.cognome}
                      </strong>
                      {logLoading ? (
                        <div style={{ marginTop: '10px', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Caricamento log...</div>
                      ) : log.length === 0 ? (
                        <div style={{ marginTop: '10px', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Nessun log disponibile.</div>
                      ) : (
                        <table style={{ width: '100%', marginTop: '10px', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ color: 'var(--text-secondary)' }}>
                              <th style={{ textAlign: 'left', paddingBottom: '6px' }}>Data</th>
                              <th style={{ textAlign: 'left', paddingBottom: '6px' }}>IP</th>
                              <th style={{ textAlign: 'left', paddingBottom: '6px' }}>Esito</th>
                            </tr>
                          </thead>
                          <tbody>
                            {log.map(l => (
                              <tr key={l.id} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                                <td style={{ padding: '5px 0' }}>{formatData(l.created_at)}</td>
                                <td style={{ padding: '5px 0', color: 'var(--text-secondary)' }}>{l.ip || '—'}</td>
                                <td style={{ padding: '5px 0' }}>
                                  <span style={{
                                    color: l.esito === 'successo' ? '#4caf50' : l.esito === 'logout' ? 'var(--color-gray)' : 'var(--color-error)',
                                    fontWeight: 600
                                  }}>
                                    {l.esito === 'successo' ? '✅' : l.esito === 'logout' ? '🚪' : '❌'} {l.esito}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {utenti.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>Nessun utente.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal azione */}
      {azione && (
        <div className="modal-overlay" onClick={() => setAzione(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            {azione.tipo === 'reset' && (
              <>
                <h3>🔑 Reset password</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '16px' }}>
                  Invia un link di reset password a <strong style={{ color: 'var(--text-primary)' }}>{azione.utente.email}</strong>.<br />
                  Il link sarà valido per 2 ore.
                </p>
              </>
            )}
            {azione.tipo === 'sospendi' && (
              <>
                <h3>🔒 Sospendi account</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '12px' }}>
                  Stai sospendendo: <strong style={{ color: 'var(--text-primary)' }}>{azione.utente.nome} {azione.utente.cognome}</strong>
                </p>
                <textarea
                  className="form-textarea"
                  placeholder="Motivo sospensione (obbligatorio)..."
                  value={motivoSospensione}
                  onChange={e => setMotivoSospensione(e.target.value)}
                  maxLength={400}
                />
              </>
            )}
            {azione.tipo === 'riattiva' && (
              <>
                <h3>✅ Riattiva account</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '16px' }}>
                  Confermi la riattivazione di <strong style={{ color: 'var(--text-primary)' }}>{azione.utente.nome} {azione.utente.cognome}</strong>?
                </p>
              </>
            )}
            {errore && <div className="alert alert-error" style={{ marginBottom: '12px' }}>{errore}</div>}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => { setAzione(null); setErrore(''); }}>Annulla</button>
              <button
                className={`btn ${azione.tipo === 'sospendi' ? 'btn-danger' : 'btn-primary'}`}
                onClick={eseguiAzione}
                disabled={actionLoading}
              >
                {actionLoading ? '⏳...' : 'Conferma'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────
// Sezione: Approvazione medici partner
// ──────────────────────────────────────────────
const SezioneMedici = () => {
  const [medici, setMedici] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStato, setFiltroStato] = useState('in_attesa');
  const [azione, setAzione] = useState(null); // { tipo, medico }
  const [nota, setNota] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [esito, setEsito] = useState('');
  const [errore, setErrore] = useState('');

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const params = filtroStato ? `?stato=${filtroStato}` : '';
      const res = await api.get(`/admin/medici${params}`);
      setMedici(res.data.medici);
    } catch {/* silenzioso */}
    finally { setLoading(false); }
  }, [filtroStato]);

  useEffect(() => { carica(); }, [carica]);

  const eseguiAzione = async () => {
    if (!azione) return;
    if (azione.tipo === 'rifiuta' && !nota.trim()) {
      setErrore('La nota di rifiuto è obbligatoria.');
      return;
    }
    setActionLoading(azione.medico.id + '_' + azione.tipo);
    setEsito('');
    setErrore('');
    try {
      if (azione.tipo === 'approva') {
        await api.patch(`/admin/medici/${azione.medico.id}/approva`, { nota });
        setEsito(`✅ Profilo di ${azione.medico.nome} ${azione.medico.cognome} approvato.`);
      } else if (azione.tipo === 'rifiuta') {
        await api.patch(`/admin/medici/${azione.medico.id}/rifiuta`, { nota });
        setEsito(`❌ Profilo rifiutato.`);
      } else if (azione.tipo === 'sospendi') {
        await api.patch(`/admin/medici/${azione.medico.id}/sospendi`, { sospendi: true });
        setEsito('🔒 Profilo medico sospeso.');
      }
      setAzione(null);
      setNota('');
      await carica();
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore.');
    } finally {
      setActionLoading(null);
    }
  };

  const formatData = (iso) => iso
    ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  return (
    <div>
      {esito && <div className="alert alert-success" style={{ marginBottom: '16px' }}>{esito}</div>}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[
          { val: 'in_attesa', label: '⏳ In attesa' },
          { val: 'approvato', label: '✅ Approvati' },
          { val: 'rifiutato', label: '❌ Rifiutati' },
          { val: 'sospeso',   label: '🔒 Sospesi' },
          { val: '',          label: '📋 Tutti' },
        ].map(f => (
          <button
            key={f.val}
            className={`btn btn-sm ${filtroStato === f.val ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFiltroStato(f.val)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? <div className="loading-msg">Caricamento...</div> : (
        <>
          {medici.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              ✅ Nessun medico in questa categoria.
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="revisioni-table">
                <thead>
                  <tr>
                    <th>Specialista</th>
                    <th>Specializzazione</th>
                    <th>Studio / Città</th>
                    <th>Stato</th>
                    <th>Registrato</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {medici.map(m => (
                    <tr key={m.id}>
                      <td>
                        <strong style={{ color: 'var(--color-medical, #4db6ac)' }}>{m.nome} {m.cognome}</strong>
                        {m.email_contatto && (
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{m.email_contatto}</div>
                        )}
                      </td>
                      <td>
                        <span style={{ fontSize: '0.85rem' }}>{m.spec_icona} {m.spec_nome}</span>
                      </td>
                      <td style={{ fontSize: '0.83rem', color: 'var(--text-secondary)' }}>
                        {m.studio_nome && <div>{m.studio_nome}</div>}
                        <div>{m.citta}{m.provincia ? ` (${m.provincia})` : ''}</div>
                      </td>
                      <td>
                        <span className={`stato-badge stato-${m.stato}`}>{m.stato.replace('_', ' ')}</span>
                      </td>
                      <td style={{ fontSize: '0.82rem' }}>{formatData(m.created_at)}</td>
                      <td>
                        <div className="rev-actions">
                          {m.stato === 'in_attesa' && (
                            <>
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => setAzione({ tipo: 'approva', medico: m })}
                                disabled={actionLoading === m.id + '_approva'}
                              >
                                ✅ Approva
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => setAzione({ tipo: 'rifiuta', medico: m })}
                              >
                                ❌ Rifiuta
                              </button>
                            </>
                          )}
                          {m.stato === 'approvato' && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setAzione({ tipo: 'sospendi', medico: m })}
                            >
                              🔒 Sospendi
                            </button>
                          )}
                          {(m.stato === 'rifiutato' || m.stato === 'sospeso') && (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => setAzione({ tipo: 'approva', medico: m })}
                            >
                              ✅ Approva
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {azione && (
        <div className="modal-overlay" onClick={() => setAzione(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>
              {azione.tipo === 'approva' ? '✅ Approva medico' :
               azione.tipo === 'rifiuta' ? '❌ Rifiuta medico' : '🔒 Sospendi medico'}
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '12px' }}>
              <strong style={{ color: 'var(--text-primary)' }}>{azione.medico.nome} {azione.medico.cognome}</strong>
              {' — '}{azione.medico.spec_nome}
            </p>
            {(azione.tipo === 'approva' || azione.tipo === 'rifiuta') && (
              <textarea
                className="form-textarea"
                placeholder={azione.tipo === 'rifiuta' ? 'Nota di rifiuto (obbligatoria)...' : 'Nota interna (opzionale)...'}
                value={nota}
                onChange={e => setNota(e.target.value)}
                maxLength={400}
              />
            )}
            {errore && <div className="alert alert-error" style={{ marginBottom: '12px' }}>{errore}</div>}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => { setAzione(null); setNota(''); setErrore(''); }}>Annulla</button>
              <button
                className={`btn ${azione.tipo === 'approva' ? 'btn-primary' : 'btn-danger'}`}
                onClick={eseguiAzione}
                disabled={!!actionLoading}
              >
                {actionLoading ? '⏳...' : 'Conferma'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────
// Sezione: Notifica globale
// ──────────────────────────────────────────────
const SezioneNotificaGlobale = () => {
  const [form, setForm] = useState({ titolo: '', messaggio: '', link: '', piano: '' });
  const [loading, setLoading] = useState(false);
  const [esito, setEsito] = useState('');
  const [errore, setErrore] = useState('');

  const invia = async (e) => {
    e.preventDefault();
    if (!form.titolo.trim() || !form.messaggio.trim()) return;
    setLoading(true);
    setEsito('');
    setErrore('');
    try {
      const res = await api.post('/admin/notifica-globale', form);
      setEsito(`✅ Notifica inviata a ${res.data.inviata_a} utenti.`);
      setForm({ titolo: '', messaggio: '', link: '', piano: '' });
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {esito && <div className="alert alert-success" style={{ marginBottom: '16px' }}>{esito}</div>}
      {errore && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{errore}</div>}

      <form className="card notifica-form" onSubmit={invia}>
        <div className="form-group">
          <label className="form-label">Titolo *</label>
          <input
            type="text"
            className="form-input"
            placeholder="Es. 🎉 Nuovi template disponibili!"
            value={form.titolo}
            onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))}
            maxLength={200}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Messaggio *</label>
          <textarea
            className="form-textarea"
            placeholder="Testo della notifica..."
            value={form.messaggio}
            onChange={e => setForm(f => ({ ...f, messaggio: e.target.value }))}
            rows={3}
            maxLength={500}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Link interno (opzionale)</label>
          <input
            type="text"
            className="form-input"
            placeholder="Es. /template"
            value={form.link}
            onChange={e => setForm(f => ({ ...f, link: e.target.value }))}
            maxLength={200}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Invia solo al piano (vuoto = tutti)</label>
          <select
            className="form-select"
            value={form.piano}
            onChange={e => setForm(f => ({ ...f, piano: e.target.value }))}
          >
            <option value="">Tutti gli utenti</option>
            <option value="free">Solo Free</option>
            <option value="pro">Solo Pro</option>
            <option value="demo">Solo Demo</option>
          </select>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? '⏳ Invio...' : '📢 Invia notifica'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ──────────────────────────────────────────────
// Componente principale PannelloAdmin
// ──────────────────────────────────────────────
const SEZIONI = [
  { id: 'stats',        label: '📊 Dashboard',         icona: '📊' },
  { id: 'revisioni',    label: '📋 Revisioni template', icona: '📋' },
  { id: 'template',     label: '📚 Tutti i template',  icona: '📚' },
  { id: 'utenti',       label: '👥 Utenti',            icona: '👥' },
  { id: 'utenti_adv',   label: '🔐 Gestione Account',  icona: '🔐' },
  { id: 'medici',       label: '🩺 Medici Partner',    icona: '🩺' },
  { id: 'notifica',     label: '📢 Notifica globale',  icona: '📢' },
];

const PannelloAdmin = () => {
  const navigate = useNavigate();
  const [sezione, setSezione] = useState(null); // inizializza dopo caricamento ruolo
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [accesso, setAccesso] = useState(true);
  const [ruolo, setRuolo] = useState(null); // 'admin' | 'moderator'

  // Sezioni visibili per ruolo
  const SEZIONI_ADMIN = ['stats', 'revisioni', 'template', 'utenti', 'utenti_adv', 'medici', 'notifica'];
  const SEZIONI_MODERATOR = ['revisioni', 'utenti'];

  const sezioniPermesse = ruolo === 'admin' ? SEZIONI_ADMIN : SEZIONI_MODERATOR;

  useEffect(() => {
    // Prima carica il ruolo
    api.get('/admin/ruolo')
      .then(res => {
        const r = res.data.ruolo;
        setRuolo(r);
        // Sezione iniziale in base al ruolo
        setSezione(r === 'admin' ? 'stats' : 'revisioni');
      })
      .catch(() => setAccesso(false));
  }, []);

  useEffect(() => {
    if (ruolo === 'admin') {
      api.get('/admin/stats')
        .then(res => setStats(res.data))
        .finally(() => setStatsLoading(false));
    } else {
      setStatsLoading(false);
    }
  }, [ruolo]);

  if (!accesso) {
    return (
      <div className="page-layout">
        <Navbar />
        <main className="page-main" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🔒</div>
          <h2 style={{ color: 'var(--text-primary)' }}>Area riservata</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>Non hai i permessi per accedere al pannello amministrativo.</p>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>← Dashboard</button>
        </main>
      </div>
    );
  }

  // Attendi caricamento ruolo
  if (!ruolo || !sezione) {
    return (
      <div className="page-layout">
        <Navbar />
        <main className="page-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 24px' }}>
          <div className="loading-msg">Caricamento...</div>
        </main>
      </div>
    );
  }

  const sezioniVisibili = SEZIONI.filter(s => sezioniPermesse.includes(s.id));

  return (
    <div className="page-layout" style={{ flexDirection: 'column' }}>
      <Navbar />
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>

        {/* Sidebar */}
        <aside className="admin-sidebar">
          <div className="admin-sidebar-title">🛠 Admin 029 Project</div>
          {/* Badge ruolo */}
          <div style={{
            margin: '0 12px 12px',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 600,
            textAlign: 'center',
            background: ruolo === 'admin' ? 'var(--color-primary)' : '#6c757d',
            color: '#fff',
            letterSpacing: '0.05em'
          }}>
            {ruolo === 'admin' ? '⚡ ADMIN' : '🛡 MODERATORE'}
          </div>
          {sezioniVisibili.map(s => (
            <div
              key={s.id}
              className={`admin-nav-link ${sezione === s.id ? 'active' : ''}`}
              onClick={() => setSezione(s.id)}
            >
              {s.label}
            </div>
          ))}
          <div style={{ marginTop: 'auto', padding: '16px', borderTop: '1px solid var(--border-color)' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/dashboard')}>
              ← App
            </button>
          </div>
        </aside>

        {/* Contenuto */}
        <main className="admin-content">
          <div className="admin-page-title">
            {SEZIONI.find(s => s.id === sezione)?.icona} {SEZIONI.find(s => s.id === sezione)?.label.replace(/^[^\s]+\s/, '')}
          </div>

          {sezione === 'stats'      && <SezioneStats stats={stats} loading={statsLoading} />}
          {sezione === 'revisioni'  && <SezioneRevisioni />}
          {sezione === 'template'   && <SezioneTuttiTemplate />}
          {sezione === 'utenti'     && <SezioneUtenti />}
          {sezione === 'utenti_adv' && <SezioneUtentiAvanzata />}
          {sezione === 'medici'     && <SezioneMedici />}
          {sezione === 'notifica'   && <SezioneNotificaGlobale />}
        </main>
      </div>
    </div>
  );
};

export default PannelloAdmin;
